import chroma from 'chroma-js';
import { nanoid } from 'nanoid';
import { type TextObject as MapTextObject, TRANSPARENT } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import RotateHandle from '../RotateHandle';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';

export default class EditTextObject extends CanvasObject {
  private text: MapTextObject;
  private point: Point;
  private onTextUpdated: (p: LngLat) => void;
  private dragOffset: Point = { x: NaN, y: NaN };
  private rotateOffset: Point = { x: NaN, y: NaN };
  private rotateHandle: RotateHandle;
  private baseZoom: number;
  private baseSize: number;
  private fontSize: number;
  private lineHeight: number;
  private bboxWidth: number;
  private lines: string[];

  constructor(id: string, surface: SurfaceBase, text: MapTextObject, onTextUpdated: (p: LngLat) => void) {
    super(id, surface);
    this.text = text;
    const currentText = this.getCurrentTextObject();
    if (!currentText) throw new Error('currentObject is not a text!');

    this.point = this.surface.toScreenCoords({
      lng: currentText.geometry.coordinates[0],
      lat: currentText.geometry.coordinates[1],
    });

    this.onTextUpdated = onTextUpdated;
    this.interactive = true;

    this.baseZoom = this.text.properties.createdAtZoom;
    this.baseSize = this.text.properties.fontSize;
    this.fontSize = this.baseSize * 2 ** (this.surface.map.getZoom() - this.baseZoom);
    this.lineHeight = this.fontSize + 5;
    this.bboxWidth = 0;
    this.lines = ['Empty Text'];

    this.rotateHandle = new RotateHandle(nanoid(), this.surface, {
      point: this.point,
      text: this.text.description,
      properties: this.text.properties,
    });

    this.rotateHandle.interactive = true;

    this.rotateHandle.on('dragStart', () => {
      this.resetRotateOffset();
      this.text.properties.rotation = 0;
      this.rotateHandle.visible = false;
      this.surface.setCursor('grabbing');
      this.renderAll();
    });

    this.rotateHandle.on('dragging', (e) => {
      this.rotateObject(e.point);
      this.surface.setCursor('grabbing');
      this.renderAll();
    });

    this.rotateHandle.on('dragEnd', (e) => {
      this.rotateHandle.visible = true;
      this.rotateObject(e.point);
      this.resetRotateOffset();
      this.surface.setCursor('default');
      this.renderAll();
    });

    this.addChild(this.rotateHandle);

    this.on('dragStart', () => {
      this.rotateHandle.visible = false;
      this.resetDragOffset();
      this.renderAll();
    });
    this.on('dragging', (e) => {
      this.rotateHandle.visible = false;
      this.dragOffset = e.diff();
      this.renderAll();
    });
    this.on('dragEnd', () => {
      this.rotateHandle.visible = true;
      this.point.x += this.dragOffset.x;
      this.point.y += this.dragOffset.y;
      this.resetDragOffset();
      this.onTextUpdated(this.surface.toMapCoords(this.point));
      this.rotateHandle.setCoords(this.point);
      this.renderAll();
    });
  }

  resetDragOffset = () => {
    this.dragOffset = { x: NaN, y: NaN };
  };

  resetRotateOffset = () => {
    this.rotateOffset = {
      x: NaN,
      y: NaN,
    };
  };

  isDragging = () => {
    return !Number.isNaN(this.dragOffset.x);
  };

  isRotating = () => {
    return !Number.isNaN(this.rotateOffset.x);
  };

  getCurrentTextObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'text') return;
    return currentObject;
  }

  refreshScreenCoords(): void {
    const currentText = this.getCurrentTextObject();
    if (!currentText) return;
    this.point = this.surface.toScreenCoords({
      lng: currentText.geometry.coordinates[0],
      lat: currentText.geometry.coordinates[1],
    });
  }

  rotateObject(point: Point): void {
    const centerX = this.point.x + this.bboxWidth / 2;
    const centerY = this.point.y + (this.lines.length / 2) * this.lineHeight - this.fontSize;

    this.rotateOffset.x = point.x - centerX;
    this.rotateOffset.y = point.y - centerY;

    const rotationAngle = Math.atan(this.rotateOffset.y / this.rotateOffset.x) + Math.PI / 2;

    if (point.x < centerX) {
      this.text.properties.rotation = -(Math.PI - rotationAngle);
    } else {
      this.text.properties.rotation = rotationAngle;
    }

    // send updates to the actual stored object to rotate when not editing the object.
    const currentText = this.getCurrentTextObject();
    if (!currentText) return;
    currentText.properties.rotation = this.text.properties.rotation;
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    if (this.fontSize < 5) {
      // can't really see, fine to skip rendering
      return;
    }

    this.lineHeight = this.fontSize + 5;

    const x = this.point.x;
    const y = this.point.y;

    ctx.resetTransform();

    const translateX = x + this.bboxWidth / 2;
    const translateY = y + (this.lines.length / 2) * this.lineHeight - this.fontSize;

    ctx.translate(translateX, translateY);
    ctx.rotate(this.text.properties.rotation);
    ctx.translate(-translateX, -translateY);

    ctx.lineWidth = 5;
    const bboxPadding = this.fontSize;
    ctx.fillRect(
      this.point.x - bboxPadding / 2,
      this.point.y - bboxPadding,
      this.bboxWidth + bboxPadding,
      this.lines.length * (this.fontSize + 5) + bboxPadding / 2,
    );
  }

  render(ctx: CanvasRenderingContext2D): void {
    const currentText = this.getCurrentTextObject();
    if (!currentText) return;
    const { description, properties } = currentText;

    this.baseZoom = properties.createdAtZoom;
    this.baseSize = properties.fontSize;

    // exponential interpolation with base 2
    this.fontSize = this.baseSize * 2 ** (this.surface.map.getZoom() - this.baseZoom);
    this.rotateHandle.bboxPadding = this.fontSize;

    if (this.fontSize < 5) {
      // can't really see, fine to skip rendering
      return;
    }

    ctx.font = `bold ${this.fontSize}px sans-serif`;
    ctx.textBaseline = 'alphabetic';

    this.lines = description.trim() === '' ? ['Empty Text'] : description.split('\n');
    this.lineHeight = this.fontSize + 5;

    let x = this.point.x;
    let y = this.point.y;

    if (this.isDragging()) {
      x = x + this.dragOffset.x;
      y = y + this.dragOffset.y;
    }

    this.bboxWidth = 0;
    for (let i = 0; i < this.lines.length; i++) {
      const textMetrics = ctx.measureText(this.lines[i]);
      this.bboxWidth = Math.max(this.bboxWidth, textMetrics.width);
    }

    const bboxTopX = x + this.bboxWidth / 2;
    const bboxTopY = y - this.fontSize;
    const bboxPadding = this.fontSize;

    this.rotateHandle.setCoords(this.point, { bboxTopX, bboxTopY });

    const translateX = x + this.bboxWidth / 2;
    const translateY = y + (this.lines.length / 2) * this.lineHeight - this.fontSize;

    // clear transformations (translate/rotate) before each render so rotation behaves correctly.
    this.surface.resetCanvasTransform(ctx);

    ctx.translate(translateX, translateY);
    ctx.rotate(this.text.properties.rotation);
    ctx.translate(-translateX, -translateY);

    // bbox
    ctx.lineWidth = 1;
    ctx.strokeStyle = properties.color;
    ctx.beginPath();
    ctx.roundRect(
      x - bboxPadding / 2,
      y - bboxPadding,
      this.bboxWidth + bboxPadding,
      this.lines.length * (this.fontSize + 5) + bboxPadding / 2,
      5,
    );
    if (properties.backgroundColor !== TRANSPARENT) {
      ctx.fillStyle = chroma(properties.backgroundColor).alpha(0.75).hex();
      ctx.stroke();
      ctx.fill();
    } else {
      ctx.stroke();
    }

    // draw text
    ctx.beginPath();
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = properties.backgroundColor === TRANSPARENT ? 'white' : properties.color;
    ctx.fillStyle = properties.color;

    for (let i = 0; i < this.lines.length; i++) {
      ctx.fillText(this.lines[i], x, y + i * this.lineHeight);
      ctx.strokeText(this.lines[i], x, y + i * this.lineHeight);
    }
  }
}
